import React, { useState } from 'react';
import {
  Search,
  Columns3,
  ChevronDown,
  AlertCircle,
  SlidersHorizontal,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/shared/ui/dropdown-menu';
import { LogoLoading } from '@/shared/ui/custom/logo-loading';
import { SearchableSelect } from '@/shared/ui/custom/searchable-select';

import HotspotsTable from './HotspotsTable';
import { useLayouts } from '../hooks/hooks';
import { Layout } from '../types/types';

interface HotspotsPageProps {
  projectId: string;
  onViewHotspot?: (
    hotspotId: number,
    photoId: number,
    coordinates: { pitch: number; yaw: number }
  ) => void;
}

// Define a special layout type for "All Hotspots"
interface LayoutOption extends Layout {
  isAllHotspots?: boolean;
}

const ALL_HOTSPOTS_OPTION: LayoutOption = {
  id: 'all-hotspots',
  name: 'All Hotspots',
  image_url: '',
  isAllHotspots: true,
};

const HotspotsPage: React.FC<HotspotsPageProps> = ({ projectId, onViewHotspot }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [subcategoryFilter, setSubcategoryFilter] = useState<number | null>(null);
  const [selectedLayout, setSelectedLayout] = useState<LayoutOption>(ALL_HOTSPOTS_OPTION);
  const [visibleColumns, setVisibleColumns] = useState({
    layoutPhotoLabel: true,
    title: true,
    createdBy: true,
    associatedObjectsCount: true,
  });

  // Fetch layouts for the project
  const { data: layouts, isLoading: layoutsLoading, error: layoutsError } = useLayouts(projectId);

  // Create combined layout options (All Hotspots + individual layouts), layouts sorted alphanumerically by name
  const layoutOptions: LayoutOption[] = React.useMemo(() => {
    const options: LayoutOption[] = [ALL_HOTSPOTS_OPTION];
    if (layouts && layouts.length > 0) {
      const sorted = [...layouts].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );
      options.push(...sorted);
    }
    return options;
  }, [layouts]);

  const toggleColumn = (columnId: keyof typeof visibleColumns) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Clear filters function matching the pattern from other tables
  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter(null);
    setSubcategoryFilter(null);
  };

  const activeFilters = searchTerm ? 1 : 0;

  const columnOptions = [
    { id: 'layoutPhotoLabel', label: 'Layout / Photo', description: 'Layout name and photo index' },
    { id: 'title', label: 'Title', description: 'Hotspot title' },
    { id: 'createdBy', label: 'Created By', description: 'User who created the hotspot' },
    { id: 'associatedObjectsCount', label: 'Associated Objects', description: 'Count of overlay photos (and future types)' },
  ];

  if (layoutsLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-64">
        <LogoLoading variant="breathing" size="lg" message="Loading hotspots..." />
      </div>
    );
  }

  if (layoutsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <div>
            <p className="text-lg font-medium text-destructive">Unable to load layouts</p>
            <p className="text-sm text-muted-foreground mt-1">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-background/30 overflow-hidden">
      {/* Enhanced header with better visual hierarchy */}
      <div className="flex-shrink-0 bg-card border-b border-border shadow-sm">
        <div className="px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6 lg:mb-6">
            <div className="space-y-1">
              <h1 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">
                Hotspots
              </h1>
              {selectedLayout && (
                <p className="text-sm text-muted-foreground">
                  <span className="text-muted-foreground">
                    {selectedLayout.isAllHotspots ? 'Viewing:' : 'Layout:'}
                  </span>{' '}
                  <span className="font-medium text-foreground">{selectedLayout.name}</span>
                </p>
              )}
            </div>

            {/* Layout selector: searchable and vertically scrollable */}
            <SearchableSelect
              options={layoutOptions.map((layout) => ({
                value: String(layout.id),
                label: layout.name,
              }))}
              value={selectedLayout ? String(selectedLayout.id) : ''}
              onValueChange={(value) => {
                const layout = layoutOptions.find((l) => String(l.id) === value);
                if (layout) setSelectedLayout(layout);
              }}
              placeholder="Select view..."
              searchPlaceholder="Search layouts..."
              emptyMessage="No layouts found."
              className="w-full lg:min-w-[200px] lg:w-auto"
              buttonClassName="justify-between"
            />
          </div>

          {/* Enhanced Search and Filter Controls */}
          <div className="flex flex-col gap-3">
            {/* Enhanced Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search hotspots, notes, or reminders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Column Visibility */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 hover:bg-accent transition-colors text-sm"
                  >
                    <Columns3 className="h-4 w-4" />
                    <span className="hidden sm:inline">Columns</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[260px]">
                  <div className="p-2">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      Show Columns
                    </div>
                    {columnOptions.map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={visibleColumns[column.id as keyof typeof visibleColumns]}
                        onCheckedChange={() =>
                          toggleColumn(column.id as keyof typeof visibleColumns)
                        }
                        className="p-3 space-x-2"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{column.label}</span>
                          <span className="text-xs text-muted-foreground">{column.description}</span>
                        </div>
                      </DropdownMenuCheckboxItem>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Clear Filters Button - matches pattern from other tables */}
              {activeFilters > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 hover:bg-accent transition-colors text-sm"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  <span className="hidden sm:inline">Clear Filters ({activeFilters})</span>
                  <span className="sm:hidden">Clear ({activeFilters})</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Container - this is the key fix for scrolling */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <HotspotsTable
          searchTerm={searchTerm}
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          subcategoryFilter={subcategoryFilter}
          visibleColumns={visibleColumns}
          selectedLayout={selectedLayout}
          projectId={projectId}
          onViewHotspot={onViewHotspot}
        />
      </div>
    </div>
  );
};

export default HotspotsPage;
